import { AnyObject } from '@common/types/queryState';
import { useCallback } from 'react';

export type MutationTrigger<TArg, TResponse> = (arg: TArg) => {
    unwrap: () => Promise<TResponse>;
};

export interface MutationState {
    isLoading: boolean;
    isSuccess: boolean;
    error?: unknown;
    reset: () => void;

    [key: string]: unknown;
}

export type RTKMutationHook<TArg, TResponse> = (
    options?: AnyObject,
) => readonly [MutationTrigger<TArg, TResponse>, MutationState];

export const useFeatureMutation = <TArg, TResponse>(
    useRTKHook: RTKMutationHook<TArg, TResponse>,
    options?: AnyObject,
) => {
    const [trigger, mutationState] = useRTKHook(options);

    const execute = useCallback(
        async (args: TArg): Promise<TResponse> => {
            try {
                return await trigger(args).unwrap();
            } catch (error) {
                console.error('Mutation failed:', error);
                throw error;
            }
        },
        [trigger],
    );

    return {
        execute,
        isLoading: mutationState.isLoading,
        isSuccess: mutationState.isSuccess,
        error: mutationState.error,
        reset: mutationState.reset,
    };
};
